import AppLayout from '@risk-smart/themed-cloudscape-components/app-layout';
import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import { GlobalHeader } from '@risksmart-app/components/src/global-header/GlobalHeader';
import Navigation from '@risksmart-app/components/src/navigation';
import { useNavMenuStore } from '@risksmart-app/components/src/navigation/useNavMenuStore';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import clsx from 'clsx';
import { type FC, type ReactNode, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router';
import ConnectedCount from 'src/components/connected-count';
import type { TCountOptions } from 'src/components/connected-count/ConnectedCount';
import NotificationsList from 'src/components/notifications-list';
import { Wizard } from 'src/components/wizard/Wizard';
import { useGetEntities } from 'src/hooks/queries/entity/useGetEntities';
import { useNavItems } from 'src/routes/useNavItems';

import { ChangeRequestLevels } from '@/components/change-request-levels/ChangeRequestLevels';
import HelpPanel from '@/components/help-panel';
import { PanelResizeHandle } from '@/components/side-panel/PanelResizeHandle';
import { SidePanel } from '@/components/side-panel/SidePanel';
import {
  getEffectivePanelWidth,
  useSidePanelStore,
} from '@/components/side-panel/useSidePanelStore';
import { useCustomOrgLogo } from '@/hooks/useCustomOrgLogo';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { logoutUrl } from '@/utils/urls';

import styles from './style.module.scss';

type Props = {
  children: ReactNode;
  panelContent?: ReactNode;
  /** Width (px) of the tools drawer when showing `panelContent`. Defaults to 350. */
  panelWidth?: number;
  globalHeader?: ReactNode;
};

export const AuthenticatedAppLayout: FC<Props> = ({
  children,
  panelContent,
  panelWidth,
  globalHeader,
}) => {
  const { t } = useTranslation(['common']);
  const { isNavigationOpen, setNavigationOpen } = useNavMenuStore();
  const navItems = useNavItems();
  const navigationWidth = isNavigationOpen ? 250 : 68;
  const maxContentWidth = 1440;
  const [toolsContent, setToolsContent, toolsLocationChanged] = useTools();
  const [searchParams, setSearchParams] = useSearchParams();
  const showEntitySelector = useIsModuleEnabled('enterprise_risk');
  const { entityIds, setEntityIds } = useEntityFilter();
  const { data: entities } = useGetEntities({ queryArgs: {} });
  const getCustomLogo = useCustomOrgLogo();
  const entityOptions = useMemo(
    () => [
      { label: t('entity.global'), value: undefined },
      ...(entities?.entity
        // @TODO: remove filter once support for nested entities is added
        .filter((entity) => !entity.children || !entity.children.length)
        .map((entity) => ({
          label: entity.Name,
          value: entity.Id,
        })) ?? []),
    ],
    [entities, t]
  );

  const {
    isOpen: isSidePanelOpen,
    close: closeSidePanel,
    isWidePanel,
    panelWidth: sidePanelWidth,
    isResizing,
    isFullScreen,
    locationChanged,
  } = useSidePanelStore();

  // Drive the panel column width via a CSS custom property so a user-resized
  // width (persisted in the store) overrides the standard/wide default.
  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const width = isSidePanelOpen
      ? getEffectivePanelWidth(sidePanelWidth, isWidePanel)
      : 0;
    gridRef.current?.style.setProperty('--side-panel-width', `${width}px`);
  }, [isSidePanelOpen, sidePanelWidth, isWidePanel]);

  let tools: ReactNode | undefined = undefined;
  let toolWidth = 350;
  const toolsType = toolsContent?.split(':')[0];
  const id = toolsContent?.split(':')[1];
  const secondaryId = toolsContent?.split(':')[2];
  switch (toolsType) {
    case 'help':
      tools = <HelpPanel />;
      break;
    case 'notifications':
      tools = <NotificationsList />;
      break;
    case 'page-content':
      tools = panelContent;
      if (panelWidth !== undefined) {
        toolWidth = panelWidth;
      }
      break;
    case 'change-request':
      if (id) {
        tools = (
          <ChangeRequestLevels parentId={id} changeRequestId={secondaryId} />
        );
      }
      break;
    case 'wizard':
      tools = <Wizard />;
      break;
    case 'ai-assistant':
      tools = <HelpPanel />;
      toolWidth = 550;
      break;
    default:
      tools = undefined;
  }

  // Use refs to track previous values to determine what changed
  const prevChatOpen = useRef(isSidePanelOpen);
  const prevToolsContent = useRef(toolsContent);

  // Handle mutual exclusivity between chat and tools
  useEffect(() => {
    const chatJustOpened = isSidePanelOpen && !prevChatOpen.current;
    const toolsJustOpened = toolsContent && !prevToolsContent.current;

    if (chatJustOpened && toolsContent) {
      // Chat was just opened and tools are open, close tools
      setToolsContent(undefined);
    } else if (toolsJustOpened && isSidePanelOpen) {
      // Tools were just opened and chat is open, close chat
      closeSidePanel();
    }

    // Update refs
    prevChatOpen.current = isSidePanelOpen;
    prevToolsContent.current = toolsContent;
  }, [isSidePanelOpen, toolsContent, setToolsContent, closeSidePanel]);

  const location = useLocation();

  useEffect(() => {
    locationChanged();
    toolsLocationChanged(location.pathname);
  }, [location, locationChanged, toolsLocationChanged]);

  // TODO check `toolsHide` is still needed in UIv3
  // Using the `toolsHide` prop causes the UI to jump, using `visibility: hidden` instead
  return (
    <div
      ref={gridRef}
      className={clsx(
        'grid grid-rows-[53px_calc(100vh-53px)] overflow-hidden h-screen print:hidden z-50',
        // Suppress the column-width transition while dragging so the panel
        // tracks the cursor instead of lagging behind it.
        !isResizing && 'transition-all duration-300 ease-out',
        styles.sidePanel,
        !isSidePanelOpen && styles.sidePanelClosed,
        isSidePanelOpen && isWidePanel && styles.sidePanelWide,
        isSidePanelOpen && !isWidePanel && styles.sidePanelStandard,
        !isNavigationOpen && styles.navigationCollapsed
      )}
    >
      <div className={'row-span-2 col-start-1 col-end-2'}>
        <Navigation
          renderCount={(props) => (
            // Counts render on both transports: every count hook
            // ConnectedCount uses is a flag-gated factory hook with a tRPC
            // branch, so V3 no longer needs them suppressed (RSP-6888).
            <ConnectedCount
              {...props}
              countName={props.countName as TCountOptions}
            />
          )}
          navItems={navItems}
          logoutUrl={logoutUrl()}
          navigationOpen={isNavigationOpen}
          setNavigationOpen={setNavigationOpen}
          customLogoUrl={getCustomLogo}
          showEntitySelector={showEntitySelector}
          entityOptions={entityOptions}
          entityFilter={entityIds}
          onEntityChange={(entityId: string | undefined) =>
            entityId ? setEntityIds([entityId]) : setEntityIds([])
          }
        />
      </div>
      <div
        className={clsx(
          'row-start-1 row-end-2 col-start-2 col-end-4',
          'max-w-[calc(100vw-var(--navigation-width,250px))]',
          'transition-width duration-300 ease-out'
        )}
      >
        {globalHeader || <GlobalHeader />}
      </div>
      <div
        className={clsx(
          'row-start-2 row-end-3 col-start-2 col-end-3',
          'overflow-y-scroll no-scrollbar transition-all duration-300 flex-1 flex flex-col'
        )}
      >
        <AppLayout
          content={
            <div className={clsx('flex-grow', styles.rsContent)}>
              {children}
            </div>
          }
          maxContentWidth={maxContentWidth}
          disableContentPaddings
          navigationOpen={false}
          onNavigationChange={() => {
            // Do nothing. Setting just to avoid warning in console
          }}
          navigationHide={true}
          toolsWidth={toolWidth}
          tools={tools}
          toolsHide={!toolsContent}
          toolsOpen={!!toolsContent}
          onToolsChange={({ detail }) => {
            if (
              !detail.open &&
              searchParams.get('showRequest') === 'true' &&
              toolsType === 'change-request'
            ) {
              setSearchParams((prev) => {
                prev.set('showRequest', 'false');

                return prev;
              });
            }

            setToolsContent(detail.open ? 'help' : undefined);
          }}
          navigationWidth={navigationWidth}
        />
      </div>
      <aside
        className={clsx(
          'overflow-hidden bg-white',
          isFullScreen && isSidePanelOpen
            ? // Full-screen: fill the main content area only — span the content
              // + panel columns (below the header, right of the nav) and paint
              // over the page content beneath it.
              'relative z-30 row-start-2 row-end-3 col-start-2 col-end-4'
            : 'relative drop-shadow-xl row-start-2 row-end-3 col-start-3 col-end-4'
        )}
      >
        {isSidePanelOpen && !isFullScreen && <PanelResizeHandle />}
        <SidePanel />
      </aside>
    </div>
  );
};
